import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import { ASSETS, SHOP_OPEN } from "../lib/content";
import { useCart } from "../lib/cart";

const HASH_LINKS = [
  { label: "Shop", href: "#shop" },
];

const ROUTE_LINKS = [
  { label: "Merch", to: "/merch" },
  { label: "Gremlins", to: "/gremlins" },
  { label: "Manifesto", to: "/manifesto" },
  { label: "Recipes", to: "/recipes" },
  { label: "Journal", to: "/journal" },
  { label: "Quiz", to: "/quiz" },
];

export const Navbar = ({ onJoin, onNav }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { totalItems, setOpen: setCartOpen } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-void/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src={ASSETS.mascot} alt="Gremlin" className="w-9 h-9 rounded-full object-cover ring-1 ring-ember/40" />
          <span className="font-display font-extrabold tracking-tight text-cream text-lg leading-none">
            GREMLIN<span className="text-ember">.</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {HASH_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => { e.preventDefault(); onNav(l.href); }}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="font-mono text-xs uppercase tracking-widest text-cream/70 hover:text-ember transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
          {ROUTE_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="font-mono text-xs uppercase tracking-widest text-cream/70 hover:text-ember transition-colors duration-200"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {SHOP_OPEN && (
          <button
            onClick={() => setCartOpen(true)}
            data-testid="nav-cart-button"
            className="relative text-cream/80 hover:text-ember transition-colors p-1.5"
            aria-label="Open bag"
          >
            <ShoppingBag size={20} />
            {totalItems > 0 && (
              <span data-testid="nav-cart-count" className="absolute -top-1 -right-1 bg-ember text-void font-mono text-[10px] font-bold min-w-[18px] min-h-[18px] rounded-full flex items-center justify-center">
                {totalItems}
              </span>
            )}
          </button>
          )}
          <button
            onClick={onJoin}
            data-testid="nav-join-button"
            className="hidden md:inline-flex font-mono text-xs uppercase tracking-widest bg-ember text-void px-5 py-2.5 rounded-full font-medium hover:bg-emberDark transition-colors duration-200"
          >
            Coffee Club
          </button>
          <button onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" className="md:hidden text-cream">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-void/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {HASH_LINKS.map((l) => (
                <a key={l.href} href={l.href} onClick={(e) => { e.preventDefault(); setOpen(false); onNav(l.href); }} data-testid={`mobile-nav-link-${l.label.toLowerCase()}`} className="font-display font-extrabold text-2xl text-cream">
                  {l.label}
                </a>
              ))}
              {ROUTE_LINKS.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-link-${l.label.toLowerCase()}`} className="font-display font-extrabold text-2xl text-cream">
                  {l.label}
                </Link>
              ))}
              <button onClick={() => { setOpen(false); onJoin(); }} data-testid="mobile-join-button" className="mt-2 font-mono text-xs uppercase tracking-widest bg-ember text-void px-5 py-3 rounded-full self-start">
                Join the Coffee Club
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
